import React from "react";
import { Values } from "@src/Tools/Values";
import { inject, observer } from "mobx-react";
import { STGrid, STPageHeader, STLinkButton, STGridButtonItem, STDeleteIcon } from "./styles";
import { observable, computed } from "mobx";
import { IManagementStore } from "@src/Stores/Management/types";
import Autocomplete, { AutocompleteInputChangeReason } from "@material-ui/lab/Autocomplete/Autocomplete";
import {
  TextField,
  Grid,
  FormControl,
  FormHelperText,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
} from "@material-ui/core";
import { AuthProvider } from "@src/Interfaces/Entity/OAuthProvider";
import { FormikProps, Formik } from "formik";
import * as Yup from "yup";
import { IAuthStore } from "@src/Stores/Auth/types";
import { RouterStore } from "mobx-react-router";
import { AssociatedAccount } from "@src/Interfaces/Entity/Person";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";

const validationSchema = Yup.object().shape({
  selectedProvider: Yup.object()
    .nullable()
    .required(() => {
      return Values.getLocalizationValue("fieldRequired");
    }),
});

interface IProps {
  auth?: IAuthStore;
  routing?: RouterStore;
  management?: IManagementStore;
}

interface IFormFields {
  selectedProvider: AuthProvider;
}

@inject("auth", "management", "routing")
@observer
export default class AssociatedAccounts extends React.Component<IProps> {
  @observable isLoading: boolean = true;

  async componentDidMount() {
    await this.props.management.getShortAuthProviders();
    await this.props.management.getAssociatedAccounts(this.props.auth.user.person.id);
    this.isLoading = false;
  }

  @computed get associatedAccounts() {
    const { auth, management } = this.props;
    return management.associatedAccounts || [];
  }

  @computed get oAuthProviders() {
    return this.props.management.shortAuthProviders.filter(
      (provider) => !this.associatedAccounts.find((account) => account.oAuthProviderId === provider.id)
    );
  }

  getProviderName = (providerId: string) => {
    const provider = this.props.management.shortAuthProviders.find((provider) => provider.id === providerId);
    return provider ? provider.name : "";
  };

  hasErrors = (formikProps: FormikProps<IFormFields>) => {
    return (
      formikProps.touched &&
      formikProps.touched.selectedProvider &&
      !!formikProps.errors.selectedProvider &&
      !!formikProps.errors.selectedProvider
    );
  };

  handleChange = (event: any, formikProps: FormikProps<IFormFields>) => {
    const selectedItem = this.props.management.shortAuthProviders.find((item) => item.name === event.target.innerText);
    formikProps.setFieldValue("selectedProvider", selectedItem || null);
  };

  handleInputChange = (reason: AutocompleteInputChangeReason, formikProps: FormikProps<IFormFields>) => {
    if (reason === "clear" || reason === "input") {
      formikProps.setFieldValue("selectedProvider", "");
    }
  };

  handleLinkAccountButtonClick = async (values: IFormFields) => {
    const { auth, routing } = this.props;
    await auth.signInViaExternalProvider(
      values.selectedProvider.id,
      auth.user.location.id,
      routing.location.pathname,
      "",
      "",
      auth.user.person.id
    );
  };

  handleDeleteAccountButton = async (account: AssociatedAccount) => {
    const { auth, management } = this.props;
    await management.deleteAssociatedAccount(account.oAuthProviderId, auth.user.person.id);
    await this.props.management.getShortAuthProviders();
    await this.props.management.getAssociatedAccounts(auth.user.person.id);
    this.forceUpdate();
  };

  render() {
    if (this.isLoading || this.props.management.shortAuthProviders.length === 0) {
      return <div />;
    }
    return (
      <React.Fragment>
        <Formik
          onSubmit={this.handleLinkAccountButtonClick}
          initialValues={{
            selectedProvider: null,
          }}
          validationSchema={validationSchema}
          render={(formikProps: FormikProps<IFormFields>) => (
            <form onSubmit={formikProps.handleSubmit}>
              <STPageHeader>{Values.getLocalizationValue("associatedAccounts")}</STPageHeader>
              <STGrid container justify="center" alignItems="center">
                <Grid item xs={9}>
                  <FormControl fullWidth error={this.hasErrors(formikProps)}>
                    <Autocomplete
                      options={this.oAuthProviders}
                      getOptionLabel={(provider) => provider.name}
                      value={formikProps.values.selectedProvider}
                      onChange={(event: any) => this.handleChange(event, formikProps)}
                      onInputChange={(event: any, value: any, reason: AutocompleteInputChangeReason) =>
                        this.handleInputChange(reason, formikProps)
                      }
                      renderInput={(params: any) => (
                        <TextField
                          {...params}
                          label={Values.getLocalizationValue("providers")}
                          error={this.hasErrors(formikProps)}
                        />
                      )}
                    />
                    {this.hasErrors(formikProps) && (
                      <FormHelperText>{formikProps.errors.selectedProvider}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <STGridButtonItem item xs={3}>
                  <STLinkButton variant="contained" onClick={formikProps.handleSubmit}>
                    {Values.getLocalizationValue("linkAccount")}
                  </STLinkButton>
                </STGridButtonItem>
              </STGrid>
              <STGrid>
                <Divider />
                <List>
                  {this.associatedAccounts.map((account, index) => {
                    const providerName = this.getProviderName(account.oAuthProviderId);
                    if (!providerName) {
                      return null;
                    }
                    return (
                      <ListItem key={index} style={{ paddingTop: 0, paddingBottom: 0 }}>
                        <ListItemIcon style={{ minWidth: "24px" }}>
                          <FiberManualRecordIcon style={{ fontSize: "0.9rem" }} />
                        </ListItemIcon>
                        <ListItemText primary={providerName} />
                        <Button onClick={() => this.handleDeleteAccountButton(account)}>
                          <ListItemIcon style={{ justifyContent: "center" }}>
                            <STDeleteIcon />
                          </ListItemIcon>
                        </Button>
                      </ListItem>
                    );
                  })}
                </List>
              </STGrid>
            </form>
          )}
        />
      </React.Fragment>
    );
  }
}
